import * as React from 'react'

import { XIcon } from 'lucide-react'
import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IAttachment } from '~/client/graph'
import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'

import FileType from '../../enums/FileType'
import { SHOW_FULLSCREEN_PREVIEW } from '../../stores/EventStore/eventConstants'
import EventsStore from '../../stores/EventStore/Events.store'
import { downloadFile } from '../../utils/util'
import PdfDocument from '../PdfDocument/PdfDocument'
import {
  getAttachmentFileIcon,
  getAttachmentFileType,
  getAttachmentText,
} from './fileAttachmentHelpers'

import './FileAttachment.scss'

interface IProps {
  attachment: IAttachment
  className?: string
  removeAttachment?: (attachment: IAttachment) => void
  customOpenFileHandler?: (
    files: IFilePreviewProperties[],
    currentIndex: number,
  ) => void
  shouldShowPdfPreview?: boolean
  isPdfPreviewSmall?: boolean
  eventsStore?: EventsStore
}

const PDF_PREVIEW_MARGIN = 10

@inject('eventsStore')
@observer
export default class FileAttachment extends React.Component<IProps> {
  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const {
      attachment,
      className,
      removeAttachment,
      shouldShowPdfPreview,
      isPdfPreviewSmall,
    } = this.props

    return (
      <div
        className={classList({
          'file-attachment pointer brada4 px12 py4 col y-center ma12 bg-white': true,
          [className]: !!className,
          'pdf-preview': shouldShowPdfPreview && !isPdfPreviewSmall,
          'small-pdf-preview': shouldShowPdfPreview && isPdfPreviewSmall,
          'relative overflow-hidden': shouldShowPdfPreview,
        })}
        onClick={this.openFile}
      >
        <div className="row">
          {getAttachmentFileIcon(attachment.fileName)}
          <div className="col">
            <div className="file-name text text-ellipsis large bold row">
              {attachment.fileName}
            </div>
            <div className="text light row">
              {attachment.size} {getAttachmentText(attachment.fileName)}
            </div>
          </div>
          {removeAttachment && (
            <span
              onClick={this.removeAttachment}
              className="file-attachment-remove-icon brada4 no-grow ml12 pa5"
            >
              <XIcon size={16} className="tw:block" />
            </span>
          )}
        </div>
        {this.fileType === FileType.Image && (
          <img className="preview-img" src={attachment.url} />
        )}
        {this.fileType === FileType.Video && (
          <div className="">
            <video className="preview-img" src={attachment.url} controls />
          </div>
        )}
        {shouldShowPdfPreview && this.fileType === FileType.Pdf && (
          <PdfDocument
            className="pt5"
            widthMargin={PDF_PREVIEW_MARGIN}
            heightMargin={PDF_PREVIEW_MARGIN}
            file={attachment.url}
          />
        )}
      </div>
    )
  }

  private get fileType(): FileType {
    return getAttachmentFileType(this.props.attachment.fileName) as FileType
  }

  @action.bound
  private removeAttachment = (event: React.MouseEvent) => {
    event.stopPropagation()
    const { attachment, removeAttachment } = this.props
    removeAttachment(attachment)
  }

  private openFile = (event: React.MouseEvent) => {
    event.stopPropagation()

    const {
      customOpenFileHandler,
      attachment: { url, fileName },
    } = this.props

    if (
      [
        FileType.Excel,
        FileType.PowerPoint,
        FileType.Word,
        FileType.OtherFile,
        FileType.Video,
      ].includes(this.fileType)
    ) {
      this.handleForceDownload()
      return
    }

    if (customOpenFileHandler) {
      return customOpenFileHandler(
        [{ fileUrl: url, fileType: this.fileType, fileName }],
        0,
      )
    }

    this.openFilePreview(url, this.fileType, fileName)
  }

  private handleForceDownload = () => {
    const {
      attachment: { url, fileName },
    } = this.props

    downloadFile(fileName, url)
  }

  @action.bound
  private openFilePreview(url: string, type: FileType, name?: string) {
    const attachment = {
      fileUrl: url,
      fileType: type,
      fileName: name,
    }

    this.props.eventsStore.dispatch(SHOW_FULLSCREEN_PREVIEW, [attachment], 0)
  }
}
